import { Box, Button, Text } from '@user-portal/wikr-components';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { selectIsDiscountLoaded } from 'store/subscriptions/selectors';

import { useSelector } from 'hooks';

import { Cross } from 'components/Icon';
import Modal from 'components/Modal';
import OutlinedButton from 'components/OutlinedButton';

import { reasons } from './constants';
import { ICancellationReasonModal, IReason } from './types';

import * as S from './styled';

const CancellationReasonModal = ({
    onSubmit,
    onCancel,
    onClose,
    trackLoad,
    captions,
    isCaliforniaFlow,
}: ICancellationReasonModal) => {
    const { t } = useTranslation();
    const [selectedReason, setReason] = useState<IReason | null>(null);

    const isDiscountLoaded: boolean = useSelector(selectIsDiscountLoaded);
    const [isButtonDisabled, setIsButtonDisabled] = useState(true);

    useEffect(() => {
        if (isDiscountLoaded) {
            setIsButtonDisabled(false);
        }
    }, [isDiscountLoaded, isButtonDisabled]);

    const handleClick = () => {
        onSubmit({ reason: selectedReason });
    };

    const handleCancel = () => {
        onCancel && onCancel({ reason: selectedReason });
        setReason(null);
    };

    const handleSelectReason = (reason: IReason) => () => {
        setReason(reason);
    };

    const handleButtonDisabled = () => {
        return !isDiscountLoaded || isButtonDisabled;
    };

    return (
        <Modal
            paddingX={0}
            paddingY={0}
            isOpen
            onClose={onClose}
            transparentOverlay={false}
            transparentBackground
            trackLoad={trackLoad}
        >
            <S.SurveyContent data-locator="cancelSubscriptionModalContainer">
                <S.ModalHeaderTitle>
                    <S.TitleText>
                        <Text type="large-text" medium>
                            {captions.headerTitle}
                        </Text>
                    </S.TitleText>
                    <S.CloseButton
                        onClick={onClose}
                        data-testid="cancelSubscriptionModalCloseButton"
                        data-locator="cancelSubscriptionModalCloseButton"
                    >
                        <Cross />
                    </S.CloseButton>
                </S.ModalHeaderTitle>
                <Box>
                    {captions?.additionalInfo && (
                        <S.SubscriptionAdditionalInfo>
                            <Text mb={8}>{captions?.additionalInfo?.title}</Text>
                            <Text color="text-secondary">{captions?.additionalInfo?.text}</Text>
                        </S.SubscriptionAdditionalInfo>
                    )}
                    <S.ModalHeader $withAdditionalInfo={Boolean(captions?.additionalInfo)}>
                        <Text type="h4" medium>
                            {captions.title}{' '}
                        </Text>
                    </S.ModalHeader>
                    <S.ReasonsContainer>
                        {reasons.map((reason) => {
                            const { title, shortKey, preselected } = reason;
                            const isSelected =
                                (!selectedReason && preselected) || shortKey === selectedReason?.shortKey;

                            return (
                                <S.ReasonItem
                                    key={shortKey}
                                    onClick={handleSelectReason(reason)}
                                    $isSelected={isSelected}
                                    data-locator={`cancelSubOption_${shortKey}`}
                                >
                                    <S.IconBox>
                                        <S.Icon $isSelected={isSelected} />
                                    </S.IconBox>
                                    <Text bold>{t(title)}</Text>
                                </S.ReasonItem>
                            );
                        })}
                    </S.ReasonsContainer>
                    <Button
                        onClick={handleClick}
                        dataLocator="obContinue"
                        mb={20}
                        disabled={!selectedReason}
                        isLoading={handleButtonDisabled()}
                    >
                        {captions.submitButton}
                    </Button>
                    {!isCaliforniaFlow && (
                        <OutlinedButton
                            dataLocator="discountOfferCancelSubscriptionButton"
                            onClick={handleCancel}
                            textColor="action-main"
                        >
                            {t('subscription.text.cancel')}
                        </OutlinedButton>
                    )}
                </Box>
            </S.SurveyContent>
        </Modal>
    );
};

export default CancellationReasonModal;
